import { Button, ButtonProps, DialogRoot, BoxProps, DialogRootProps } from "@chakra-ui/react"
import { useContext, ReactNode, useRef } from "react"
import { DialogContext } from "../../App";
import { SPACING } from "../../generic/config";
import { DialogBackdrop, DialogBody, DialogCloseTrigger, DialogContent, DialogFooter, DialogHeader } from "@/components/ui/dialog";
import Text from "../Text";

type openDialogProps = {
    title: string;
    body: ReactNode;
    buttons: dialogButton[];
    size?: DialogRootProps["size"];
    forcedHeight?: string;
};

type dialogButton = {
    title: string;
    onClick: () => void;
    colorScheme?: ButtonProps["colorScheme"];
    backgroundColor?: BoxProps["backgroundColor"];
};

const useDialog = () => {
    const { dialogs, setDialogs } = useContext(DialogContext);
    const open = useRef(false);
    const onOpen = () => open.current = true;
    const onClose = () => open.current = false;

    const closeDialogRef = useRef<() => void>(() => { });

    const closeDialog = () => closeDialogRef.current?.();

    const openDialog = ({ title, body, buttons, size = "md", forcedHeight }: openDialogProps) => {
        closeDialogRef.current = () => {
            setDialogs(dialogs.filter((d) => d !== dialog));
            onClose();
        };
        onOpen();
        const desiredHeight = forcedHeight || "auto";

        const dialog = (
            <>
                <DialogRoot
                    motionPreset="slide-in-bottom"
                    placement="center"
                    open={open.current}
                    role="alertdialog"
                    size={size}
                >
                    <DialogBackdrop />
                    <DialogContent position="absolute">
                        <DialogHeader><Text variant="title">{title}</Text></DialogHeader>
                        <DialogCloseTrigger onClick={closeDialog} />
                        <DialogBody minH={desiredHeight} maxH={desiredHeight} overflowY="auto" p={SPACING}>
                            {body}
                        </DialogBody>
                        <DialogFooter>
                            {buttons.map((button, index) => (
                                <Button mx={SPACING / 4} key={index} colorScheme={button.colorScheme} backgroundColor={button.backgroundColor} onClick={
                                    () => {
                                        button.onClick();
                                        closeDialog();
                                    }
                                }>
                                    {button.title}
                                </Button>
                            ))}

                        </DialogFooter>
                    </DialogContent>
                </DialogRoot>
            </>
        );

        setDialogs([...dialogs, dialog]);
    }

    return { openDialog, closeDialog };
};

export default useDialog;
